import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown, Star } from "lucide-react";
import { AGENCY } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";
import { MaskLine } from "@/components/site/Motion";

export default function Hero() {
  const { openQuote } = useQuote();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1566168900250-5d22ae8ef4e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Southern live oak at golden hour in Louisiana"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-brand-ink/60 to-brand-ink/55" />
        <div className="absolute inset-0 bg-brand-ink/25" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-6"
        >
          <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-brand-sand text-brand-sand" />)}</div>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-cream/80">
            Baton Rouge · Independent since 2005
          </span>
        </motion.div>

        <h1 className="font-serif text-brand-cream text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tight max-w-5xl">
          <MaskLine delay={0.15}>Insurance that</MaskLine>
          <MaskLine delay={0.3}><span className="italic text-brand-sand">feels like family.</span></MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-xl text-brand-cream/85 text-base sm:text-lg leading-relaxed"
        >
          Auto, home, life, and commercial coverage for Louisiana families and businesses —
          shopped across 10+ carriers by neighbors who answer the phone.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button onClick={() => openQuote()} data-testid="hero-quote-btn"
            className="rounded-full bg-brand-clay text-white font-medium px-8 py-4 hover:bg-[#9e4c3e] active:scale-95 transition-all">
            Get a Free Quote
          </button>
          <a href={AGENCY.phoneHref} data-testid="hero-call-btn"
            className="rounded-full border border-brand-cream/40 text-brand-cream font-medium px-8 py-4 flex items-center gap-2 hover:bg-brand-cream/10 transition-colors">
            <Phone className="h-4 w-4" /> Call {AGENCY.phone}
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-6 right-8 hidden md:flex items-center gap-2 text-brand-cream/60">
        <ArrowDown className="h-4 w-4 animate-bounce" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Scroll</span>
      </div>
    </section>
  );
}
